import { read_timeline_plain_text, read_timeline_text } from "./timeline_normalization";

// Formats one event timestamp for the compact timeline metadata row.
export const read_timeline_date_label = (value) => {
    const date = new Date(value || 0);
    const labels = {
        true: "",
        false: date.toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false
        })
    };

    return labels[Number.isNaN(date.getTime()) || date.getTime() <= 0];
};

// Reads a visible person or system name from one actor payload.
export const read_timeline_actor_label = (event, t) => {
    const actor = read_timeline_text(event?.by?.name || event?.by?.email || t("System"));

    if (actor === "Erised Quest history") {
        return t("Questad");
    }

    return actor === "Unknown author" ? t(actor) : actor;
};

// Replaces internal application codenames in user-facing timeline text.
export const read_timeline_product_text = (value) => {
    return read_timeline_text(value)
        .replace(/\bniffler\b/gi, "Leads")
        .replace(/\bcerebro\b/gi, "Tasks")
        .replace(/\bgodfather\b/gi, "Offers")
        .replace(/\berised\b/gi, "Accounts");
};

// Displays the production product name for one internal source application.
export const read_timeline_source_label = (sourceApp, t, eventType = "") => {
    const erisedLabels = {
        TAG: "Payments",
        DEFAULT: "Accounts"
    };
    const erisedLabel = erisedLabels[read_timeline_text(eventType).toUpperCase()] || erisedLabels.DEFAULT;
    const sourceLabels = {
        niffler: "Leads",
        leads: "Leads",
        cerebro: "Tasks",
        tasks: "Tasks",
        godfather: "Offers",
        offers: "Offers",
        erised: erisedLabel
    };

    return t(sourceLabels[read_timeline_text(sourceApp).toLowerCase()] || read_timeline_product_text(sourceApp));
};

// Reads a visible scalar or object label used by event detail renderers.
export const read_timeline_value_label = (value, emptyLabel = "-") => {
    const isObject = Boolean(value && typeof value === "object");
    const objectValue = {
        true: value,
        false: {}
    }[isObject];
    const objectLabel = read_timeline_text(objectValue.name || objectValue.brand || objectValue.status || objectValue.username || objectValue.email || objectValue.id || objectValue._id);
    const scalarLabel = read_timeline_text(value);
    const labels = {
        object: objectLabel || emptyLabel,
        scalar: scalarLabel || emptyLabel
    };

    const labelKey = {
        true: "object",
        false: "scalar"
    }[isObject];

    return labels[labelKey];
};

// Formats the scheduled SASS transition without repeating handoff details.
const read_sass_timeline_title = (event) => {
    const previousStatus = read_timeline_value_label(event?.from?.status || event?.data?.previousStatus || event?.from);
    const schedule = read_timeline_date_label(event?.data?.rescheduledAt || event?.data?.schedule || event?.to?.meetingDate || event?.to?.schedule);
    const scheduleSuffix = {
        true: ` @ ${schedule}`,
        false: ""
    }[Boolean(schedule)];

    return `${previousStatus} > SASS${scheduleSuffix}`;
};

// Reads the translated title associated with one canonical event type.
export const read_timeline_title = (event, t) => {
    const titleKeys = {
        BRAND_CREATED: "Brand created",
        COMMENT: "",
        TO_CALL: "Moved to To Call",
        IN_PROGRESS: "Work started",
        STALLING: "Callback scheduled",
        LOST: "Brand marked Lost",
        SELLER_ASSIGNED: "Seller assigned",
        SELLER_REMOVED: "Seller removed",
        BRAND_UPDATE: "Brand updated",
        SALES_SESSION: "Sales session",
        SASS_SESSION: "Meeting session",
        BRAND_ARCHIVED: "Brand archived",
        OWNER_REGISTERED: "Owner registered",
        CLIENT_CREATED: "Client created",
        CLIENT_LINKED: "Client linked",
        CLIENT_UNLINKED: "Client unlinked",
        OFFER_CREATED: "Offer created",
        OFFER_PAID: "Offer payment confirmed",
        OFFER_ACCEPTED: "Offer accepted",
        SASS_SET: "SASS scheduled",
        SASS_RESCHEDULE: "SASS rescheduled",
        SASS_IN_PROGRESS: "SASS in progress",
        SASS_LOST: "SASS marked Lost",
        SASS_PAID: "SASS payment confirmed",
        COMMUNICATION: "Communication",
        WORK_COMPLETED: "Work completed",
        PROSPECT_MERGED: "Prospect merged",
        BRAND_MERGED: "Brand merged",
        TAG: "Tag recorded",
        SYNC_WARNING: "Sync warning",
        SYNC_RECOVERED: "Sync restored",
        LEGACY_EVENT: "Legacy event",
        LEGACY_ACTIVITY: "Activity history",
        GENERIC: "Timeline event"
    };
    const titleReaders = {
        SASS: () => read_sass_timeline_title(event),
        DEFAULT: () => titleKeys[event?.type] || titleKeys.GENERIC
    };
    const titleReader = titleReaders[event?.type] || titleReaders.DEFAULT;

    return t(titleReader());
};

// Reads field changes from current arrays or historical from/to snapshots.
export const read_timeline_changes = (event) => {
    const savedChanges = event?.data?.changes || [];
    const from = event?.from && typeof event.from === "object" ? event.from : { value: event?.from };
    const to = event?.to && typeof event.to === "object" ? event.to : { value: event?.to };
    const generatedChanges = [...new Set([...Object.keys(from), ...Object.keys(to)])].filter((field) => {
        return JSON.stringify(from[field]) !== JSON.stringify(to[field]);
    }).map((field) => ({
        field,
        from: from[field],
        to: to[field]
    }));
    const defaultChanges = {
        true: savedChanges,
        false: generatedChanges
    };
    const singleChangeLabels = {
        SELLER_ASSIGNED: "Seller",
        SELLER_REMOVED: "Seller",
        PROSPECT_MERGED: "Prospect",
        BRAND_MERGED: "Brand"
    };
    const singleChangeLabel = singleChangeLabels[event?.type];
    const changes = {
        true: [{
            field: singleChangeLabel,
            from: event?.from,
            to: event?.to
        }],
        false: defaultChanges[Boolean(savedChanges.length)]
    };

    return changes[Boolean(singleChangeLabel)].map((change) => ({
        field: read_timeline_text(change?.field || change?.name || "Field"),
        from: read_timeline_value_label(change?.from ?? change?.oldValue),
        to: read_timeline_value_label(change?.to ?? change?.newValue)
    }));
};

// Reads plain event text while retaining intentional line breaks.
export const read_timeline_note_label = (event) => {
    const note = read_timeline_plain_text(event?.note);
    const generatedNote = [
        /^sass meeting\s+\d{4}-\d{2}-\d{2}[t\s].*$/i,
        /^sass handoff$/i,
        /^handed off$/i
    ].some((pattern) => pattern.test(note));
    const noteLabels = {
        true: "",
        false: note
    };

    return noteLabels[generatedNote];
};
